import React, { useState, useEffect, useCallback, useMemo } from "react";
import ContactTableDynamic from "../ContactTable";
import { API_ENDPOINT } from "../../../utils/constants";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useApiEp from "../../../hooks/useApiEp";
import SectionLabel from "../../../widgets/SectionLabel";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContactListGroup from "./ContactListGroup";
import { toast } from "react-toastify";
import IndexSelector from "./IndexSelector";
import AddToTargetBtn from "./AddToTargetBtn";

const RdCampaignContactGoup = ({
  campaignPost,
  setCampaignPost,
  setActiveStep,
}) => {
  const { data: contactList, isLoading: loadingList } =
    useApiEp(`/user/group-list`);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [contacts, setContacts] = useState([]);
  const [mappingPositions, setMappingPositions] = useState([]);
  const [headerMappingPosition, setHeaderMappingPosition] = useState({
    type: "dynamic",
    param_header: "",
  });

  const isHeaderTextParams = useMemo(() => {
    const headerComp = campaignPost?.template?.components?.find(
      (c) => c?.type?.toUpperCase() === "HEADER"
    );
    return (
      headerComp?.format?.toUpperCase() === "TEXT" &&
      (headerComp?.example?.header_text ?? []).length > 0
    );
  }, [campaignPost?.template]);

  useEffect(() => {
    if (campaignPost?.contactsList) {
      setContacts(campaignPost.contactsList);
      setSelectedGroup(campaignPost.contactGroup);
    }
  }, [campaignPost?.contactsList, campaignPost?.contactGroup]);

  useEffect(() => {
    if (campaignPost.bodyParams.length > 0 && !campaignPost.isStaticParams) {
      if (
        campaignPost?.contactMapping &&
        campaignPost.contactMapping.length > 0
      ) {
        setMappingPositions(campaignPost.contactMapping);
      } else {
        setMappingPositions(
          campaignPost.bodyParams.map((item, index) => ({
            type: "dynamic",
            [`param_${index + 1}`]: "",
          }))
        );
      }
    }
  }, [campaignPost.bodyParams, campaignPost.isStaticParams]);

  useEffect(() => {
    if (isHeaderTextParams) {
      if (campaignPost?.headerMapping) {
        setHeaderMappingPosition(campaignPost.headerMapping);
      } else {
        setHeaderMappingPosition({ type: "dynamic", param_header: "" });
      }
    }
  }, [isHeaderTextParams, campaignPost?.headerMapping]);

  useEffect(() => {
    if (
      contacts.length > 0 &&
      (!campaignPost.startIndex || !campaignPost.endIndex)
    ) {
      setCampaignPost((prev) => ({
        ...prev,
        startIndex: 0,
        endIndex: contacts.length,
      }));
    }
  }, [contacts, setCampaignPost]);

  const handleMappingChange = (paramIndex, field, newValue) => {
    setMappingPositions((prev) => {
      return prev.map((item, index) => {
        if (index !== paramIndex) return item;
        const updatedItem = { ...item };
        updatedItem[field] = newValue;
        return updatedItem;
      });
    });
  };

  const handleHeaderMappingChange = (field, newValue) => {
    setHeaderMappingPosition((prev) => ({ ...prev, [field]: newValue }));
  };

  const generateTargetAudience = () => {
    if (isHeaderTextParams && headerMappingPosition.param_header === "") {
      toast.error("Please enter a value for the header parameter");
      return;
    }

    const checkFields = mappingPositions.filter(
      (item, index) => item[`param_${index + 1}`] === ""
    );
    if (checkFields.length > 0) {
      toast.error("Please enter all field values in the mapping");
      return;
    }
    if (campaignPost.endIndex <= campaignPost.startIndex) {
      toast.error("End index must be greater than start index");
      return;
    }

    const newContacts = [];
    const slicedContacts = contacts.slice(
      campaignPost.startIndex,
      campaignPost.endIndex
    );

    slicedContacts.forEach((citem) => {
      const contact = { Numbers: citem.phoneNumber };

      if (isHeaderTextParams) {
        if (headerMappingPosition.type === "dynamic") {
          contact["param_header"] = citem[headerMappingPosition.param_header];
        } else {
          contact["param_header"] = headerMappingPosition.param_header;
        }
      }

      mappingPositions.forEach((item, index) => {
        if (item.type === "dynamic") {
          const field = item[`param_${index + 1}`];
          contact[`param_${index + 1}`] = citem[field];
        } else {
          contact[`param_${index + 1}`] = item[`param_${index + 1}`];
        }
      });

      newContacts.push(contact);
    });

    setCampaignPost((prev) => ({
      ...prev,
      contacts: newContacts,
      contactsList: contacts,
      contactMapping: mappingPositions,
      headerMapping: headerMappingPosition,
      contactGroup: selectedGroup,
      startIndex: campaignPost.startIndex,
      endIndex: campaignPost.endIndex,
    }));
    toast.success(`Added ${newContacts.length} contacts to target audience`);
    setActiveStep(2);
  };

  const contactFields =
    contacts.length > 0
      ? Object.keys(contacts[0]).filter(
          (item) => item !== "_id" && item !== "phoneNumber"
        )
      : [];

  return (
    <Container maxWidth="md">
      <Autocomplete
        size="small"
        loading={loadingList}
        sx={{ minWidth: 164, my: 2 }}
        value={selectedGroup}
        onChange={(event, newV) => {
          setSelectedGroup(newV);
        }}
        options={contactList || []}
        renderInput={(params) => <TextField {...params} label="Group Name" />}
      />

      {selectedGroup ? (
        <ContactListGroup
          group={selectedGroup}
          onLoaded={(list) => {
            setContacts(list);
          }}
        />
      ) : (
        <Typography variant="body1" color="GrayText">
          Please select contact group
        </Typography>
      )}

      {contacts.length > 0 && (
        <Box mt={3}>
          <SectionLabel>Available contact fields:</SectionLabel>
          <Box
            sx={{ my: 2, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {contactFields.map((item) => (
                <Chip key={item} variant="outlined" label={item} />
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      {/* Header param mapping */}
      {contacts.length > 0 && isHeaderTextParams && (
        <Box mt={3}>
          <SectionLabel>Header Parameter Mapping</SectionLabel>
          <Box
            sx={{ my: 2, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography fontWeight="bold">Header</Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={headerMappingPosition.type}
                  label="Type"
                  onChange={(e) =>
                    handleHeaderMappingChange("type", e.target.value)
                  }
                >
                  <MenuItem value="dynamic">Dynamic</MenuItem>
                  <MenuItem value="static">Static</MenuItem>
                </Select>
              </FormControl>
              {headerMappingPosition.type === "dynamic" ? (
                <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
                  <InputLabel>Select contact field</InputLabel>
                  <Select
                    value={headerMappingPosition.param_header}
                    label="Select contact field"
                    onChange={(e) =>
                      handleHeaderMappingChange("param_header", e.target.value)
                    }
                  >
                    {contactFields.map((field) => (
                      <MenuItem key={field} value={field}>
                        {field}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  label="Enter static value"
                  value={headerMappingPosition.param_header}
                  onChange={(e) =>
                    handleHeaderMappingChange("param_header", e.target.value)
                  }
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Body params mapping */}
      {contacts.length > 0 &&
        campaignPost.bodyParams.length > 0 &&
        !campaignPost.isStaticParams && (
          <Box mt={3}>
            <SectionLabel>
              Configure parameters - map to contact fields or set static values
            </SectionLabel>
            {mappingPositions.map((param, index) => (
              <Box
                my={3}
                key={`param-${index}`}
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Typography fontWeight="bold">{index + 1}.</Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={param.type}
                    label="Type"
                    onChange={(e) =>
                      handleMappingChange(index, "type", e.target.value)
                    }
                  >
                    <MenuItem value="dynamic">Dynamic</MenuItem>
                    <MenuItem value="static">Static</MenuItem>
                  </Select>
                </FormControl>
                {param.type === "dynamic" && (
                  <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
                    <InputLabel>Select contact field</InputLabel>
                    <Select
                      value={param[`param_${index + 1}`]}
                      label="Select contact field"
                      onChange={(e) =>
                        handleMappingChange(
                          index,
                          `param_${index + 1}`,
                          e.target.value
                        )
                      }
                    >
                      {contactFields.map((field) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {param.type === "static" && (
                  <TextField
                    size="small"
                    fullWidth
                    label="Enter static value"
                    value={param[`param_${index + 1}`]}
                    onChange={(e) =>
                      handleMappingChange(
                        index,
                        `param_${index + 1}`,
                        e.target.value
                      )
                    }
                  />
                )}
              </Box>
            ))}
          </Box>
        )}

      <IndexSelector
        campaignPost={campaignPost}
        setCampaignPost={setCampaignPost}
        contacts={contacts}
      />
      {selectedGroup && <AddToTargetBtn onClick={generateTargetAudience} />}
    </Container>
  );
};

export default RdCampaignContactGoup;
